import { Icons } from '@solvhealth/jigsaw-v1';

export type ConsumerServiceCategory = {
  slug: string;
  name: string;
  examples: string;
  description?: string;
  icon: keyof typeof Icons;
  inPerson: boolean;
  telemed: boolean;
  symptomChecker: boolean;
  asyncChat: boolean;
  hidden: boolean;
  sortOrder: number;
  new?: boolean;
  isExternalPartnership?: boolean;
  kids: Partial<{
    hidden: boolean;
    examples: string;
    description: string;
    sortOrder: number;
  }>;
};

export const consumerServiceCategories: ReadonlyArray<ConsumerServiceCategory> = [
  {
    sortOrder: 7,
    name: 'Illness & infection',
    examples: 'Sore throat, flu, UTI, asthma, abscess...',
    description:
      'Don’t let illness stop you in your tracks. Find care for strep throat, the flu, UTI, pink eye, and any other illness or infection. ',
    icon: 'IllnessCough',
    slug: 'illness-infection',
    inPerson: true,
    telemed: true,
    symptomChecker: true,
    asyncChat: true,
    hidden: false,
    kids: {
      sortOrder: 2,
      examples: 'Sore throat, flu, ear infections, RSV, pink eye ...',
      description: `Don't let your little one's illness stop you in your tracks. Find care for strep throat, the flu, RSV, pink eye, and any other illness or infection.`,
    },
  },
  {
    sortOrder: 8,
    name: 'Injury & pain',
    examples: 'Burns, cuts, earwax removal, PT...',
    description:
      'We’ll help you get care fast and recover quickly from injuries. Book a visit for burns, cuts, breaks and more today. ',
    icon: 'InjuryFinger',
    slug: 'injury-pain',
    inPerson: true,
    telemed: true,
    symptomChecker: true,
    asyncChat: true,
    hidden: false,
    kids: {
      sortOrder: 3,
      examples: 'Cuts, burns, bruises, earwax removal...',
      description: `When your kiddos fall down, we're here to help. Book a visit for cuts, burns, breaks, bruises and more today.`,
    },
  },
  {
    sortOrder: 2,
    name: 'Vaccinations & immunizations',
    examples: 'Flu, Tdap, HPV, measles, COVID...',
    description:
      'Stay on top of vaccinations and immunizations for you and your family. Find flu, COVID, TDaP and other vaccinations near you. ',
    icon: 'Syringe',
    slug: 'vaccines',
    inPerson: true,
    telemed: false,
    symptomChecker: false,
    asyncChat: false,
    hidden: false,
    kids: {
      sortOrder: 5,
      examples: 'Flu, Tdap, HPV, measles, COVID...',
      description: `Stay on top of your family's vaccinations and immunizations. Find flu, COVID, TDaP and other vaccinations near you. `,
    },
  },
  {
    sortOrder: 1,
    name: 'Labs & tests',
    examples: 'STD tests, food allergy test, metabolic panel...',
    description:
      'Learn more about your health through labs and tests. Book a metabolic blood panel, allergy test, A1C test or another lab test quickly and easily.',
    icon: 'Drops',
    slug: 'labs',
    inPerson: true,
    telemed: false,
    symptomChecker: false,
    asyncChat: false,
    hidden: false,
    kids: {
      sortOrder: 8,
      examples: 'Blood work, allergies tests, hemoglobin test...',
      description: `Stay on top of your kid's health through labs and tests. Book a blood panel, allergy test, hemoglobin test or another lab test quickly and easily.`,
    },
  },
  {
    sortOrder: 11,
    name: 'COVID-19',
    examples: 'Tests, vaccinations, boosters, treatments...',
    description:
      'We’re here to help you and your family navigate COVID-19. Find a test, vaccine, or booster near you. ',
    icon: 'Covid',
    slug: 'covid',
    inPerson: true,
    telemed: true,
    symptomChecker: true,
    asyncChat: true,
    hidden: false,
    kids: {
      sortOrder: 7,
      examples: 'Tests, vaccinations, boosters, treatments...',
      description:
        'We’re here to help you and your family navigate COVID-19. Find a test, vaccine, or booster near you. ',
    },
  },
  {
    sortOrder: 5,
    name: 'Men’s & women’s health',
    examples: 'UTIs, STIs, vaccinations, exams...',
    description:
      "From routine pap smears for women to testosterone level tests for men, stay healthy with men's and women’s health services. ",
    icon: 'Stethoscope',
    slug: 'mens-womens',
    inPerson: true,
    telemed: true,
    symptomChecker: true,
    asyncChat: true,
    hidden: false,
    kids: {
      hidden: true,
      sortOrder: Infinity,
    },
  },
  {
    sortOrder: 6,
    name: "Children's health",
    examples: 'School or camp physicals, vaccinations...',
    description:
      'Help your little ones stay healthy and happy with convenient and compassionate healthcare. Find and book school physicals, treatment for illness, and more.',
    icon: 'Heart',
    slug: 'childrens',
    inPerson: true,
    telemed: true,
    symptomChecker: true,
    asyncChat: true,
    hidden: false,
    kids: {
      sortOrder: 1,
      examples: 'School or camp physicals, vaccinations...',
      description: `Help your little ones stay healthy and happy with convenient and compassionate healthcare. Find and book school physicals, treatment for illness, and more.`,
    },
  },
  {
    sortOrder: 10,
    name: 'Chronic conditions',
    examples: 'Asthma, diabetes, gout, autoimmune...',
    description:
      'We’re here to help manage chronic conditions. From asthma to diabetes, find the best care to support your health. ',
    icon: 'MedicalStar',
    slug: 'chronic',
    inPerson: true,
    telemed: false,
    symptomChecker: false,
    asyncChat: false,
    hidden: false,
    kids: {
      sortOrder: 10,
      examples: 'Asthma, diabetes, epilepsy, cerebal palsy...',
      description:
        'We’re here to help manage any chronic conditions your child may have. From asthma to diabetes, find the best care to support their health. ',
    },
  },
  {
    sortOrder: 9,
    name: 'Skin health',
    examples: 'Acne, rashes, shingles, poison ivy...',
    description:
      'Protect yourself and your family against skin conditions. Whether it’s bug bites, shingles, or an abscess treatment, we’ve got your back.',
    icon: 'IllnessRash',
    slug: 'skin-health',
    inPerson: true,
    telemed: false,
    symptomChecker: false,
    asyncChat: false,
    hidden: false,
    kids: {
      sortOrder: 4,
      examples: 'Rashes, hand, foot and mouth disease, eczema...',
      description: `Protect your little one against skin conditions. Whether it's bug bites, hand, foot and mouth disease, or a rash, we've got your back.`,
    },
  },
  {
    sortOrder: 12,
    name: 'Medical imaging',
    new: false,
    isExternalPartnership: false,
    examples: 'Mammogram, X-ray, Ultrasound, CT scan, MRI scan, Dexa scan...',
    description:
      'The best way to schedule high-quality imaging appointments near you. Use your insurance or opt for discounted self-pay exams.',
    icon: 'InjuryBrokenBone',
    slug: 'medical-imaging',
    inPerson: true,
    telemed: false,
    symptomChecker: false,
    asyncChat: false,
    hidden: false,
    kids: {
      sortOrder: 9,
      examples: 'X-ray, ultrasound, MRI...',
      description: `The best way to schedule high-quality imaging appointments for you and your kids. Use your insurance or opt for discounted self-pay exams.`,
    },
  },
  {
    sortOrder: 3,
    new: true,
    isExternalPartnership: true,
    name: 'Mental health',
    examples:
      'Individual therapy, couples therapy, anxiety, depression, stress, personal growth...',
    description:
      'A simple, supportive approach to mental health care. Find a therapist today — virtual and in-person care available.',

    icon: 'MedicalHead',
    slug: 'mental-health',
    inPerson: false,
    telemed: true,
    symptomChecker: false,
    asyncChat: false,
    hidden: false,
    kids: {
      hidden: true,
      sortOrder: Infinity,
    },
  },
];
